import { useTranslation } from 'react-i18next';
import { useId, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateShipperFreight, type UpdateShipperFreightPayload } from '../../api/endpoints/admin';
import type { ShipmentResponse } from '../../api/types';
import formStyles from '../../components/domain/FormModal.module.css';
import { Button } from '../../components/ui/Button';
import confirmStyles from '../../components/ui/ConfirmDialog.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { SupportAction } from '../../components/ui/SupportAction';
import { toast } from '../../components/ui/Toast';
import { useDialogDismiss } from '../../components/ui/dialogDismiss';
import { curr } from '../../utils/price';
import {
  SHIPPER_FREIGHT_REASON_MAX,
  shipperFreightAmountValid,
  shipperFreightErrorMessage,
  shipperFreightLocked,
  shipperFreightReasonValid,
} from './shipperFreight';
import { DialogTitleRow } from '../../help/DialogTitleRow';

interface Props {
  shipment: ShipmentResponse;
  onClose: () => void;
}

/**
 * Edit the shipper final after award (#221, R10 step B): whole-rupee amount and a required
 * reason, confirmed with the amount before it is sent (UX 3). On success the detail cache
 * is replaced with the server's response, so the card shows the server's figures (P3).
 */
export function EditShipperFreightDialog({ shipment, onClose }: Props) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const { t } = useTranslation();
  const [amount, setAmount] = useState(
    shipment.shipperFacingFinalPrice != null ? String(shipment.shipperFacingFinalPrice) : '',
  );
  const [reason, setReason] = useState('');
  const [step, setStep] = useState<'form' | 'confirm'>('form');
  const sending = useRef(false);

  const save = useMutation<ShipmentResponse, Error, UpdateShipperFreightPayload>({
    mutationFn: (body) => updateShipperFreight(shipment.id, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'shipments', 'detail', shipment.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['payments', 'shipments', shipment.id] });
      toast.success(`Shipper final set to ${curr(updated.shipperFacingFinalPrice)}.`);
      onClose();
    },
    onError: (error) => {
      if (shipperFreightLocked(error)) {
        void queryClient.invalidateQueries({ queryKey: ['admin', 'shipments', 'detail', shipment.id] });
      }
    },
    onSettled: () => {
      sending.current = false;
    },
  });

  const busy = save.isPending;
  const amountValid = shipperFreightAmountValid(amount);
  const reasonValid = shipperFreightReasonValid(reason);
  const locked = shipperFreightLocked(save.error);
  const dirty =
    amount !== String(shipment.shipperFacingFinalPrice ?? '') || reason.trim() !== '';
  const dismiss = useDialogDismiss({ kind: 'form', dirty, busy, onClose });

  const send = () => {
    if (sending.current || !amountValid || !reasonValid) return;
    sending.current = true;
    save.mutate({ shipperFreight: Number(amount), reason: reason.trim() });
  };

  const review = (e: FormEvent) => {
    e.preventDefault();
    if (amountValid && reasonValid) setStep('confirm');
  };

  return (
    <DialogShell
      dismiss={dismiss}
      labelledBy={titleId}
      scrimClassName={confirmStyles.backdrop}
      panelClassName={confirmStyles.modal}
    >
      <div className={confirmStyles.header}>
        <DialogTitleRow context="order-prices" title={t('shipments.editShipperFreightDialog.title')}>
          <h3 id={titleId} className={confirmStyles.title}>
            {t('shipments.editShipperFreightDialog.title')}
          </h3>
        </DialogTitleRow>
        <button
          type="button"
          className={confirmStyles.closeBtn}
          onClick={dismiss.requestClose}
          disabled={busy}
          aria-label={t('shipments.editShipperFreightDialog.closeDialog')}
        >
          &times;
        </button>
      </div>

      {step === 'form' ? (
        <form onSubmit={review} className={confirmStyles.body}>
          <div className={confirmStyles.description}>
            {t('shipments.editShipperFreightDialog.description', { carrierPrice: curr(shipment.finalPrice) })}
          </div>
          <div>
            <label className={formStyles.label} htmlFor="shipper-freight-amount">
              {t('shipments.editShipperFreightDialog.amountLabel')}
            </label>
            <input
              id="shipper-freight-amount"
              className={formStyles.input}
              type="number"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-autofocus
            />
            {amount !== '' && !amountValid && (
              <p className={formStyles.hint}>{t('shipments.editShipperFreightDialog.amountInvalid')}</p>
            )}
          </div>
          <div className={confirmStyles.reasonWrapper}>
            <label className={formStyles.label} htmlFor="shipper-freight-reason">
              {t('shipments.editShipperFreightDialog.reasonLabel')}
            </label>
            <textarea
              id="shipper-freight-reason"
              className={confirmStyles.textarea}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('shipments.editShipperFreightDialog.reasonPlaceholder')}
            />
            {reason !== '' && !reasonValid && (
              <p className={formStyles.hint}>{t('shipments.editShipperFreightDialog.reasonInvalid')}</p>
            )}
            <p className={formStyles.hint}>
              {reason.trim().length}/{SHIPPER_FREIGHT_REASON_MAX}
            </p>
          </div>
          <div className={confirmStyles.actions}>
            <Button variant="secondary" onClick={dismiss.requestClose}>
              {t('common.actions.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={!amountValid || !reasonValid}>
              {t('shipments.editShipperFreightDialog.reviewChange')}
            </Button>
          </div>
        </form>
      ) : (
        <div className={confirmStyles.body}>
          <div className={confirmStyles.description}>
            {t('shipments.editShipperFreightDialog.confirmFrom', { from: curr(shipment.shipperFacingFinalPrice) })}
            <strong>{curr(Number(amount))}</strong>
            {t('shipments.editShipperFreightDialog.confirmCarrier', { carrier: curr(shipment.finalPrice) })}
          </div>
          {save.error && (
            <div className={formStyles.error} role="alert">
              {shipperFreightErrorMessage(save.error)}
            </div>
          )}
          {save.error && !locked && <SupportAction />}
          <div className={confirmStyles.actions}>
            {locked ? (
              <Button variant="primary" onClick={onClose}>
                {t('common.actions.close')}
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setStep('form')} disabled={busy}>
                  {t('shipments.editShipperFreightDialog.back')}
                </Button>
                <Button variant="primary" onClick={send} busy={busy}>
                  {busy ? 'Saving…' : save.error ? 'Try again' : `Save ${curr(Number(amount))}`}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </DialogShell>
  );
}
